import React from 'react';
import { Button, notification } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import type { ErrorReportContext } from '@/components/TabsLayout/TabErrorBoundary';

/**
 * Separate sessionStorage key for API-triggered error reports.
 * TabErrorBoundary continues using ERROR_REPORT_CONTEXT_KEY for React errors.
 */
export const API_ERROR_REPORT_CONTEXT_KEY = 'utx_api_error_report_context';

// ---------------------------------------------------------------------------
// Support center availability
// ---------------------------------------------------------------------------

let _supportCenterEnabled = false;

export function setSupportCenterEnabled(enabled: boolean): void {
  _supportCenterEnabled = enabled;
}

export function isSupportCenterEnabled(): boolean {
  return _supportCenterEnabled;
}

// ---------------------------------------------------------------------------
// Sensitive-data masking (PCI DSS 4.0.1 / SOC 2 / OWASP)
// ---------------------------------------------------------------------------

/**
 * Comprehensive sensitive field names for query-string, header, and JSON masking.
 * Covers: Authentication, MFA/OTP, Financial/PCI, PII, Secrets, HTTP headers.
 * Longer compound names listed before catch-all short names so both match.
 */
const SENSITIVE_FIELDS = [
  // Authentication
  'password(?:[_-]?confirm(?:ation)?)?', 'access[_-]?token', 'refresh[_-]?token', 'api[_-]?key', 'api[_-]?secret',
  // MFA / OTP
  'otp[_-]?code', 'mfa[_-]?token', '_?yotp[_-]?token', 'totp[_-]?code', 'verification[_-]?code',
  // Financial / PCI
  'cvv', 'cvc', 'card[_-]?number', 'account[_-]?number', 'routing[_-]?number', 'swift[_-]?code',
  // PII
  'ssn', 'social[_-]?security[_-]?number', 'tax[_-]?id', 'date[_-]?of[_-]?birth',
  'mobile', 'phone', 'phone[_-]?number',
  // Secrets
  'secret[_-]?key', 'private[_-]?key', 'encryption[_-]?key',
  // Catch-all short names (substring-match handles compound variants like accessToken)
  'token', 'secret', 'auth', 'credential', 'authorization',
  'otp', 'pin', 'pan', 'iban',
  'cookie', 'set[_-]?cookie', 'proxy[_-]?authorization',
].join('|');

const MASKING_RULES: Array<{ pattern: RegExp; replacement: string }> = [
  // JWT / Bearer tokens (including non-JWT bearer values)
  { pattern: /Bearer\s+[A-Za-z0-9\-_]+(?:\.[A-Za-z0-9\-_]+)*/gi, replacement: 'Bearer [REDACTED]' },
  // Standalone JWTs (eyJ…)
  { pattern: /eyJ[A-Za-z0-9\-_]{10,}\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]*/g, replacement: '[JWT_REDACTED]' },
  // Email addresses
  { pattern: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, replacement: '[EMAIL_REDACTED]' },
  // PAN (13-19 digit card numbers) — mask to first 6 / last 4 per PCI DSS
  { pattern: /\b(\d{6})\d{3,9}(\d{4})\b/g, replacement: '$1******$2' },
  // Phone numbers (international E.164 and common formats)
  { pattern: /\+?\d[\d\s\-()]{7,}\d/g, replacement: '[PHONE_REDACTED]' },
  // Sensitive query-string / header / assignment params (key=value or key: value)
  { pattern: new RegExp(`((?:${SENSITIVE_FIELDS})[=:]\\s*)[^\\s&"']+`, 'gi'), replacement: '$1[REDACTED]' },
  // JSON string values: "sensitiveField": "value"
  { pattern: new RegExp(`("(?:${SENSITIVE_FIELDS})"\\s*:\\s*)"[^"]*"`, 'gi'), replacement: '$1"[REDACTED]"' },
];

/**
 * Scrub PII, secrets, and payment data from a string before persisting
 * to sessionStorage. Each rule is applied sequentially.
 */
export function maskSensitiveData(value: string): string {
  if (!value) return value;
  let masked = value;
  for (const { pattern, replacement } of MASKING_RULES) {
    // Reset lastIndex for global regexes re-used across calls
    pattern.lastIndex = 0;
    masked = masked.replace(pattern, replacement);
  }
  return masked;
}

/**
 * Safely stringify a value and truncate to avoid blowing up sessionStorage.
 * Returns undefined for nullish/empty input.
 */
const MAX_BODY_LENGTH = 2000;

export function safeStringify(value: unknown, maxLength = MAX_BODY_LENGTH): string | undefined {
  if (value === null || value === undefined) return undefined;
  try {
    const raw = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
    if (!raw || raw === '{}' || raw === '[]') return undefined;
    return raw.length > maxLength ? `${raw.slice(0, maxLength)}\n... [truncated]` : raw;
  } catch {
    return '[unserializable]';
  }
}

/**
 * Deep-mask all string values in an ErrorReportContext.
 */
export function maskContext(ctx: ErrorReportContext): ErrorReportContext {
  const mask = (v: string | undefined) => (v ? maskSensitiveData(v) : v);
  return {
    ...ctx,
    errorMessage: maskSensitiveData(ctx.errorMessage),
    errorName: maskSensitiveData(ctx.errorName),
    errorStack: mask(ctx.errorStack),
    componentStack: mask(ctx.componentStack),
    url: maskSensitiveData(ctx.url),
    path: ctx.path,
    requestUrl: mask(ctx.requestUrl),
    errorCode: mask(ctx.errorCode),
    userAgent: ctx.userAgent,
    timestamp: ctx.timestamp,
    tabKey: ctx.tabKey,
    httpStatus: ctx.httpStatus,
    requestMethod: ctx.requestMethod,
    requestBody: mask(ctx.requestBody),
    responseBody: mask(ctx.responseBody),
  };
}

// ---------------------------------------------------------------------------
// Context builders
// ---------------------------------------------------------------------------

export type HttpErrorReportParams = {
  status: number;
  message: string;
  errorCode?: string;
  requestUrl?: string;
  requestMethod?: string;
  requestBody?: unknown;
  responseBody?: unknown;
};

export type TrpcErrorReportParams = {
  message: string;
  errorCode?: string;
  procedurePath?: string;
  requestInput?: unknown;
  responseData?: unknown;
};

export function buildHttpErrorReportContext(
  params: HttpErrorReportParams
): ErrorReportContext {
  return maskContext({
    errorMessage: params.message,
    errorName: `HTTP ${params.status}`,
    url: window.location.href,
    path: window.location.pathname,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    httpStatus: params.status,
    requestUrl: params.requestUrl,
    requestMethod: params.requestMethod,
    errorCode: params.errorCode,
    requestBody: safeStringify(params.requestBody),
    responseBody: safeStringify(params.responseBody),
  });
}

export function buildTrpcErrorReportContext(
  params: TrpcErrorReportParams
): ErrorReportContext {
  return maskContext({
    errorMessage: params.message,
    errorName: 'tRPC Error',
    url: window.location.href,
    path: window.location.pathname,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    errorCode: params.errorCode,
    requestUrl: params.procedurePath ? `/trpc/${params.procedurePath}` : undefined,
    requestBody: safeStringify(params.requestInput),
    responseBody: safeStringify(params.responseData),
  });
}

// ---------------------------------------------------------------------------
// API-error description formatter (markdown)
// ---------------------------------------------------------------------------

/**
 * Keep generated markdown within the nodes supported by TiptapEditor.
 * Unsupported structures such as tables are flattened into a single paragraph.
 */
export function formatApiErrorDescription(ctx: ErrorReportContext): string {
  const endpoint = ctx.requestUrl ?? ctx.path;
  const method = ctx.requestMethod ? `${ctx.requestMethod} ` : '';
  const statusLine = ctx.httpStatus ? `- **HTTP Status:** \`${ctx.httpStatus}\`` : null;
  const codeLine = ctx.errorCode ? `- **Error Code:** \`${ctx.errorCode}\`` : null;

  const requestSection = ctx.requestBody
    ? ['## Request Body', '```json', ctx.requestBody, '```', '']
    : [];

  const responseSection = ctx.responseBody
    ? ['## Response Body', '```json', ctx.responseBody, '```', '']
    : [];

  return ([
    '# API Error Report',
    '',
    `- **Endpoint:** \`${method}${endpoint}\``,
    statusLine,
    codeLine,
    `- **Page:** \`${ctx.path}\``,
    `- **Time:** \`${ctx.timestamp}\``,
    '',
    '## Error Message',
    '```',
    ctx.errorMessage,
    '```',
    '',
    ...requestSection,
    ...responseSection,
    '---',
    '',
    '## What I was doing when this happened',
    '[Please describe what you were doing when this error occurred]',
    '',
    '## Steps to reproduce',
    '1. ',
    '2. ',
    '3. ',
  ] as (string | null)[])
    .filter((line): line is string => line !== null)
    .join('\n');
}

/**
 * Derive the pre-fill title for the Report Issue form.
 * Format: "API Error: {endpoint} - {errorMessage}"
 */
export function buildApiErrorTitle(ctx: ErrorReportContext): string {
  const endpoint = ctx.requestUrl ?? ctx.path;
  const raw = `API Error: ${endpoint} - ${ctx.errorMessage}`;
  return raw.length > 120 ? `${raw.substring(0, 117)}...` : raw;
}

/**
 * Derive severity from HTTP status code.
 * 500+ → High, everything else → Medium.
 */
export function deriveSeverityFromStatus(status?: number): 'High' | 'Medium' {
  return status && status >= 500 ? 'High' : 'Medium';
}

// ---------------------------------------------------------------------------
// Notification button
// ---------------------------------------------------------------------------

/**
 * Returns a ReactNode for the `btn` prop of Ant Design's notification.error().
 * On click: stores error context in sessionStorage under
 * API_ERROR_REPORT_CONTEXT_KEY and navigates to report-issue.
 */
export function renderReportIssueBtn(
  context: ErrorReportContext,
  notificationKey: string
): React.ReactNode {
  if (!_supportCenterEnabled) {
    return undefined;
  }

  return (
    <Button
      type="link"
      size="small"
      icon={<ExclamationCircleOutlined />}
      style={{ color: '#8b5cf6', padding: 0 }}
      onClick={() => {
        sessionStorage.setItem(
          API_ERROR_REPORT_CONTEXT_KEY,
          JSON.stringify(context)
        );
        notification.destroy(notificationKey);
        window.location.href = '/support-center/report-issue';
      }}
    >
      Report Issue
    </Button>
  );
}
